"use client";
import { z } from "zod";
import Link from "next/link";
import { useState } from "react";
import axios from "@config/axios";
import { AxiosError } from "axios";
import { encrypt } from "@lib/encrypt";
import { useForm } from "react-hook-form";
import { titles, userTypes } from "@data";
import { Input } from "@components/ui/input";
import { toast } from "@components/ui/toaster";
import { Button } from "@components/ui/button";
import { Combobox } from "@components/ui/combobox";
import { useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { PhoneInput } from "@components/ui/phone-input";
import usePasswordToggle from "@hooks/usePasswordToggle";
import { IndividualRegisterSchema } from "@schemas/register";
import { useRouter, useSearchParams } from "next/navigation";
import { RadioGroup, RadioGroupItem } from "@components/ui/radio-group";
import { ContactRound, Handshake, LockKeyhole, Mail, UserRoundPlus } from "lucide-react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@components/ui/form";

type RegisterIndividualFormProps = {
	inviteCode?: string;
	email?: string;
	profileType?: string;
};

const RegisterIndividualForm = ({ inviteCode, email, profileType }: RegisterIndividualFormProps) => {
	const router = useRouter();
	const searchParams = useSearchParams();
	const urlParams = new URLSearchParams(searchParams?.toString() || "");
	const [onRegisterToast, setOnRegisterToast] = useState<string | number>();
	const { inputIcon: pwdInputIcon, inputType: pwdInputType } = usePasswordToggle();
	const { inputIcon: cPwdInputIcon, inputType: cPwdInputType } = usePasswordToggle();

	const defaultValues: z.infer<typeof IndividualRegisterSchema> = {
		title: "",
		firstName: "",
		middleName: "",
		lastName: "",
		email: email ?? "",
		contactNo: "",
		password: "",
		confirmPassword: "",
		profileType: profileType ?? "",
	};

	const form = useForm<z.infer<typeof IndividualRegisterSchema>>({
		defaultValues,
		mode: "onTouched",
		resolver: zodResolver(IndividualRegisterSchema),
	});

	const onRegister = async (values: z.infer<typeof IndividualRegisterSchema>) => {
		setOnRegisterToast(toast.loading("Loading...", { description: "Registering account..." }));

		const { title, firstName, middleName, lastName, email, contactNo, profileType } = values;
		const password = encrypt(values?.password);
		const confirmPassword = encrypt(values?.confirmPassword);

		await axios.post(`/api/profile/individual/register${inviteCode ? `?inviteCode=${inviteCode}` : ""}`, {
			title,
			firstName,
			middleName,
			lastName,
			password,
			confirmPassword,
			email,
			contactNo,
			profileType,
		});
	};

	const { mutate, isPending } = useMutation({
		mutationFn: onRegister,
		onSuccess: () => {
			toast.success("Success!", { id: onRegisterToast, description: "Registered successfully!" });
			router.replace(`/auth/login${urlParams.toString() ? `?${urlParams.toString()}` : ""}`);
		},
		onError: (error: unknown) => {
			toast.error("Error!", { id: onRegisterToast, description: (error as AxiosError<string>)?.response?.data || "Internal server error!" });
		},
	});

	return (
		<div className="flex w-full max-w-5xl flex-col items-center justify-center gap-6 sm:justify-start">
			<div className="flex w-full flex-col items-center justify-center gap-1">
				<h1 className="text-center text-2xl font-semibold">{`Welcome Aboard`}</h1>
				<p className="text-muted-foreground text-center text-sm">{"Enter your details to continue"}</p>
			</div>
			<Form {...form}>
				<form className="flex w-full max-w-sm flex-col items-center justify-stretch gap-7 md:max-w-2xl" onSubmit={form.handleSubmit(values => mutate(values))}>
					<div className="grid w-full grid-cols-1 justify-center gap-6 md:grid-cols-2">
						<FormField
							control={form.control}
							name="title"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>
										Title<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<Combobox emptyLabel="No Titles" label="Title" options={titles} placeholder="Select your title" startContent={<Handshake size={16} />} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="firstName"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>
										First Name<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<Input placeholder="Enter your first name" startContent={<ContactRound size={16} />} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="middleName"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>Middle Name</FormLabel>
									<FormControl>
										<Input placeholder="Enter your middle name" startContent={<ContactRound size={16} />} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="lastName"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>
										Last Name<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<Input placeholder="Enter your last name" startContent={<ContactRound size={16} />} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="email"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>
										Email<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<Input disabled={!!email} placeholder="Enter your email" startContent={<Mail size={16} />} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="contactNo"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>
										Contact No.<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<PhoneInput placeholder="Enter your contact no." {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="password"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>
										Password<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<Input endContent={pwdInputIcon} placeholder="Enter your password" startContent={<LockKeyhole size={16} />} type={pwdInputType} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="confirmPassword"
							render={({ field }) => (
								<FormItem className="w-full">
									<FormLabel>
										Confirm Password<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<Input endContent={cPwdInputIcon} placeholder="Confirm your Password" startContent={<LockKeyhole size={16} />} type={cPwdInputType} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="profileType"
							render={({ field }) => (
								<FormItem className="space-y-3 md:col-span-2">
									<FormLabel>
										Register As<span className="text-destructive">*</span>
									</FormLabel>
									<FormControl>
										<RadioGroup defaultValue={field.value} disabled={!!profileType} onValueChange={field.onChange}>
											{userTypes?.map(userType => (
												<FormItem className="flex items-center gap-1" key={userType?.uuid}>
													<FormControl>
														<RadioGroupItem value={userType?.value} />
													</FormControl>
													<FormLabel className="font-normal">{userType?.label}</FormLabel>
												</FormItem>
											))}
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
					<div className="grid w-full grid-cols-1 items-center justify-center gap-6 md:grid-cols-2">
						<Button className="w-full" disabled={isPending} size="lg" type="submit" variant="default">
							<UserRoundPlus size={16} />
							Register
						</Button>
						<span className="order-last text-xs md:order-first md:text-sm">
							{`Already have an account? `}
							<Link className="text-secondary text-xs font-medium md:text-sm" href="/auth/login">
								Login Now
							</Link>
						</span>
					</div>
				</form>
			</Form>
		</div>
	);
};

export default RegisterIndividualForm;
